"use client";

import React, { useState, useEffect } from "react";
import { Logo } from "../../ui/icons/logo";
import { getQuizResult } from "../utils/utils";
import type { QuestionHistoryEntry } from "../types";
import { setQuizState } from "../utils/utils";

interface QuizFetchingResultProps {
  config: Record<string, unknown>;
  questionHistory: QuestionHistoryEntry[];
  onComplete: () => void;
}

export function QuizFetchingResult({
  config,
  questionHistory,
  onComplete,
}: QuizFetchingResultProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const answers = questionHistory.reduce((acc, entry) => {
      acc[entry.questionId] = entry.selectedOptionIds;
      return acc;
    }, {} as Record<string, number[]>);
    const results = getQuizResult(answers);
    setQuizState({ results: results ?? undefined });
  }, [config]);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((prev) => Math.min(prev + 2, 100));
    }, 100);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (progress === 100) {
      onComplete();
    }
  }, [progress, onComplete]);

  return (
    <div className="py-5 flex-1 flex flex-col">
      <div className="flex flex-col flex-1 bg-[#F3F4ED] rounded-[13px]">
        <div className="flex w-full flex-col items-center px-6 gap-12 py-[34px] rounded-[13px] overflow-hidden overscroll-none">
          <Logo />
          <div className="flex flex-col items-center gap-6 w-full">
            <div className="flex flex-col items-center gap-6">
              <div className="relative w-24 h-24">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="98"
                  height="98"
                  viewBox="0 0 98 98"
                  fill="none"
                  className="transform -rotate-90"
                >
                  <path
                    d="M97.5 49C97.5 75.7858 75.7858 97.5 49 97.5C22.2142 97.5 0.5 75.7858 0.5 49C0.5 22.2142 22.2142 0.5 49 0.5C75.7858 0.5 97.5 22.2142 97.5 49ZM5.16085 49C5.16085 73.2117 24.7883 92.8391 49 92.8391C73.2117 92.8391 92.8391 73.2117 92.8391 49C92.8391 24.7883 73.2117 5.16085 49 5.16085C24.7883 5.16085 5.16085 24.7883 5.16085 49Z"
                    fill="#E6E8DE"
                  />
                  <circle
                    cx="49"
                    cy="49"
                    r="44.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4.6608"
                    strokeLinecap="round"
                    strokeDasharray={`${(progress * 279.6) / 100} 279.6`}
                    className="text-primary transition-all duration-300 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[#112D35] title-18">
                    {progress}%
                  </span>
                </div>
              </div>

              <h2 className="text-primary title-22 text-center max-w-[240px]">
                Hang tight creating your sample hormone chart!
              </h2>
            </div>

            <div className="flex flex-col gap-[18px] items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="156"
                height="1"
                viewBox="0 0 156 1"
                fill="none"
              >
                <path
                  d="M0.25 0.5H155.75"
                  stroke="url(#paint0_linear_fetch1)"
                  strokeOpacity="0.13"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_fetch1"
                    x1="0.25"
                    y1="1"
                    x2="155.75"
                    y2="1"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#122D35" stopOpacity="0" />
                    <stop offset="0.498469" stopColor="#122D35" />
                    <stop offset="1" stopColor="#122D35" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="23"
                    viewBox="0 0 22 23"
                    fill="none"
                  >
                    <path
                      d="M14.5672 10.4483C14.3114 10.7822 13.9176 10.9816 13.497 10.9903C13.0773 10.9793 12.6847 10.7803 12.4277 10.4483"
                      stroke="#122D35"
                      strokeWidth="1.375"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.57307 10.4483C9.31745 10.782 8.92403 10.9814 8.50379 10.9903C8.08382 10.9793 7.69097 10.7803 7.43359 10.4483"
                      stroke="#122D35"
                      strokeWidth="1.375"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.0001 19.1419C14.4417 19.1402 17.4572 16.8373 18.3649 13.5176C19.3932 13.4056 20.1721 12.5372 20.1721 11.5028C20.1721 10.4685 19.3932 9.60012 18.3649 9.48809C17.4596 6.16654 14.4429 3.86206 11.0001 3.86206C7.55738 3.86206 4.54068 6.16654 3.6353 9.48809C2.60704 9.60012 1.82812 10.4685 1.82812 11.5028C1.82812 12.5372 2.60704 13.4056 3.6353 13.5176C4.54301 16.8373 7.55853 19.1402 11.0001 19.1419Z"
                      stroke="#122D35"
                      strokeWidth="1.375"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.0383 14.2577C11.8707 15.2763 10.1304 15.2763 8.96289 14.2577"
                      stroke="#122D35"
                      strokeWidth="1.375"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.98242 7.09008C10.2784 7.30599 10.6349 7.42311 11.0013 7.4248C11.9703 7.40282 12.7445 6.61093 12.7445 5.6416C12.7445 4.67227 11.9703 3.88038 11.0013 3.8584"
                      stroke="#122D35"
                      strokeWidth="1.375"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-[#122D35] title-14 text-center">
                  25k+ Inito babies
                </p>
              </div>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="156"
                height="1"
                viewBox="0 0 156 1"
                fill="none"
              >
                <path
                  d="M0.25 0.5H155.75"
                  stroke="url(#paint0_linear_fetch2)"
                  strokeOpacity="0.13"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_fetch2"
                    x1="0.25"
                    y1="1"
                    x2="155.75"
                    y2="1"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#122D35" stopOpacity="0" />
                    <stop offset="0.498469" stopColor="#122D35" />
                    <stop offset="1" stopColor="#122D35" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4].map((star) => (
                    <div key={star} className="w-3.5 h-3.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 0.5L8.5716 5.33688H13.6574L9.5429 8.32624L11.1145 13.1631L7 10.1738L2.8855 13.1631L4.4571 8.32624L0.342604 5.33688H5.4284L7 0.5Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                  ))}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                  >
                    <path
                      d="M7 0.5L8.5716 5.33688H13.6574L9.5429 8.32624L11.1145 13.1631L7 10.1738L2.8855 13.1631L4.4571 8.32624L0.342604 5.33688H5.4284L7 0.5Z"
                      fill="#BEBCAB"
                    />
                    <mask
                      id="mask0_fetch"
                      style={{ maskType: "alpha" }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="7"
                      height="14"
                    >
                      <rect y="0.5" width="7" height="13" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_fetch)">
                      <path
                        d="M7 0.5L8.5716 5.33688H13.6574L9.5429 8.32624L11.1145 13.1631L7 10.1738L2.8855 13.1631L4.4571 8.32624L0.342604 5.33688H5.4284L7 0.5Z"
                        fill="black"
                      />
                    </g>
                  </svg>
                </div>

                <div className="flex gap-1 items-center">
                  <p className="text-primary title-14">
                    Rated 4.3
                    <span className="ml-1">on Amazon with 1400+ ratings</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
